import { KeyRound, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ApiProblem, changePassword } from "../lib/api";

export function PasswordChangeDialog({
  onCancel,
  onChanged,
}: {
  readonly onCancel: () => void;
  readonly onChanged: () => Promise<void>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (currentPassword === newPassword) {
      setError("Choose a password different from the current password.");
      return;
    }
    if (newPassword !== confirmation) {
      setError("New password entries do not match.");
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await changePassword(currentPassword, newPassword);
      await onChanged();
    } catch (failure) {
      setError(
        failure instanceof ApiProblem &&
          failure.problem.code === "CURRENT_PASSWORD_INCORRECT"
          ? "The current password is incorrect."
          : "The password could not be changed. Try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="dialog-layer" role="presentation">
      <section
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
      >
        <div className="dialog-title">
          <div>
            <KeyRound aria-hidden="true" />
            <h2 id="change-password-title">Change password</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Close password dialog"
            disabled={submitting}
            onClick={onCancel}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <p>
          Every active BalikTANiman session will be revoked. Sign in again on
          each device after the change.
        </p>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Current password
            <input
              type="password"
              autoComplete="current-password"
              minLength={12}
              maxLength={128}
              required
              disabled={submitting}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </label>
          <label>
            New password
            <input
              type="password"
              autoComplete="new-password"
              minLength={12}
              maxLength={128}
              required
              disabled={submitting}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </label>
          <label>
            Confirm new password
            <input
              type="password"
              autoComplete="new-password"
              minLength={12}
              maxLength={128}
              required
              disabled={submitting}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </label>
          {error === undefined ? null : (
            <p className="field-error" role="alert">
              {error}
            </p>
          )}
          <div className="dialog-actions">
            <button
              className="button secondary"
              type="button"
              disabled={submitting}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="button primary"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Changing…" : "Change password"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
